<template>
  <div class="relative">
    <!-- iOS Safari: un solo input oculto con one-time-code -->
    <input
      ref="autofillInput"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      tabindex="-1"
      aria-hidden="true"
      class="absolute opacity-0 w-px h-px -z-10 pointer-events-none"
      @input="onAutofillInput"
    />

    <div class="flex gap-2 justify-center" @paste="onPaste">
      <input
        v-for="(digit, index) in digits"
        :key="index"
        :ref="(el) => setInputRef(el, index)"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        :value="digit"
        :disabled="disabled"
        :aria-label="`Dígito ${index + 1} de ${length}`"
        :class="cellClass(index, digit)"
        @input="onInput(index, $event)"
        @beforeinput="onBeforeInput(index, $event)"
        @keydown="onKeyDown(index, $event)"
        @paste="onPaste"
        @focus="onFocus(index)"
        @blur="onBlur"
      />
    </div>
  </div>
</template>

<script>
  export default {
    name: "OtpInput",
    props: {
      modelValue: {
        type: String,
        default: "",
      },
      length: {
        type: Number,
        default: 6,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      invalid: {
        type: Boolean,
        default: false,
      },
      autofocus: {
        type: Boolean,
        default: true,
      },
      /** Web OTP API (autocompletar SMS en Android). Desactivado por ahora. */
      webOtp: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["update:modelValue", "complete"],
    data() {
      return {
        focusedIndex: null,
        inputRefs: [],
        webOtpAbort: null,
      };
    },
    computed: {
      digits() {
        const value = this.modelValue ?? "";
        return value
          .split("")
          .concat(Array(this.length).fill(""))
          .slice(0, this.length);
      },
    },
    watch: {
      modelValue(val) {
        this.syncDigitInputs(val);
        if (this.webOtp && !val && !this.webOtpAbort) {
          this.startWebOtpListener();
        }
      },
    },
    mounted() {
      if (this.webOtp) {
        this.startWebOtpListener();
      }
      if (!this.autofocus || this.disabled) return;
      this.$nextTick(() => {
        const firstEmpty = this.digits.findIndex((d) => d === "");
        const idx = firstEmpty === -1 ? 0 : firstEmpty;
        this.inputRefs[idx]?.focus?.();
      });
    },
    beforeUnmount() {
      this.stopWebOtpListener();
    },
    methods: {
      setInputRef(el, index) {
        if (el) this.inputRefs[index] = el;
      },
      syncDigitInputs(value) {
        const code = String(value ?? "");
        this.$nextTick(() => {
          for (let i = 0; i < this.length; i += 1) {
            if (this.inputRefs[i]) {
              this.inputRefs[i].value = code[i] || "";
            }
          }
        });
      },
      applyCode(raw) {
        const code = String(raw ?? "")
          .replace(/\D/g, "")
          .slice(0, this.length);
        if (!code) return;

        const autofill = this.$refs.autofillInput;
        if (autofill) {
          autofill.value = code;
        }

        this.$emit("update:modelValue", code);
        this.syncDigitInputs(code);

        this.$nextTick(() => {
          if (code.length === this.length) {
            this.$emit("complete", code);
          }
        });
      },
      onAutofillInput(event) {
        if (this.disabled) return;
        this.applyCode(event.target.value);
        event.target.value = "";
      },
      startWebOtpListener() {
        if (!this.webOtp || typeof window === "undefined") return;
        if (!("OTPCredential" in window)) return;
        this.stopWebOtpListener();
        this.webOtpAbort = new AbortController();
        navigator.credentials
          .get({
            otp: { transport: ["sms"] },
            signal: this.webOtpAbort.signal,
          })
          .then((credential) => {
            this.webOtpAbort = null;
            const code = credential?.code;
            if (code) {
              this.applyCode(code);
            }
          })
          .catch(() => {
            this.webOtpAbort = null;
          });
      },
      stopWebOtpListener() {
        if (this.webOtpAbort) {
          this.webOtpAbort.abort();
          this.webOtpAbort = null;
        }
      },
      cellClass(index, digit) {
        const base =
          "w-11 h-14 text-center text-xl font-bold rounded-lg border-2 transition-all duration-150 focus:outline-none";
        if (this.disabled) {
          return `${base} bg-light-gray text-dark-gray border-light-gray cursor-not-allowed opacity-60`;
        }
        if (this.invalid) {
          return `${base} bg-white border-red-warning text-black`;
        }
        if (this.focusedIndex === index) {
          return `${base} bg-white border-aces ring-2 ring-aces ring-opacity-30 text-black`;
        }
        if (digit) {
          return `${base} bg-white border-aces border-opacity-50 text-black`;
        }
        return `${base} bg-light-gray border-light-gray hover:border-dark-gray text-black`;
      },
      emitValue(newDigits) {
        const next = newDigits.join("");
        this.$emit("update:modelValue", next);
        if (next.length === this.length && !next.includes("")) {
          this.$emit("complete", next);
        }
      },
      onInput(index, event) {
        if (this.disabled) return;
        const raw = event.target.value ?? "";
        const digitsOnly = raw.replace(/\D/g, "");

        if (digitsOnly.length > 1) {
          event.target.value = this.digits[index] || "";
          this.applyCode(digitsOnly);
          const focusIdx = Math.min(digitsOnly.length, this.length) - 1;
          this.$nextTick(() => this.inputRefs[focusIdx]?.focus?.());
          return;
        }

        const digit = digitsOnly.slice(-1);
        const next = [...this.digits];
        next[index] = digit;
        event.target.value = digit;
        this.emitValue(next);
        if (digit && index < this.length - 1) {
          this.$nextTick(() => this.inputRefs[index + 1]?.focus?.());
        }
      },
      onBeforeInput(index, event) {
        if (this.disabled) return;
        const pasteTypes = [
          "insertFromPaste",
          "insertFromDrop",
          "insertReplacementText",
        ];
        if (!pasteTypes.includes(event.inputType)) return;

        const text = event.data ?? "";
        const digitsOnly = text.replace(/\D/g, "");
        if (digitsOnly.length <= 1) return;

        event.preventDefault();
        this.applyCode(digitsOnly);
        const focusIdx = Math.min(digitsOnly.length, this.length) - 1;
        this.$nextTick(() => this.inputRefs[focusIdx]?.focus?.());
      },
      onKeyDown(index, e) {
        if (this.disabled) return;
        if (e.key === "Backspace") {
          if (this.digits[index] === "" && index > 0) {
            e.preventDefault();
            this.inputRefs[index - 1]?.focus?.();
          } else {
            const next = [...this.digits];
            next[index] = "";
            this.emitValue(next);
          }
        } else if (e.key === "ArrowLeft" && index > 0) {
          e.preventDefault();
          this.inputRefs[index - 1]?.focus?.();
        } else if (e.key === "ArrowRight" && index < this.length - 1) {
          e.preventDefault();
          this.inputRefs[index + 1]?.focus?.();
        }
      },
      onPaste(e) {
        if (this.disabled) return;
        e.preventDefault();
        const pasted = (e.clipboardData?.getData("text") ?? "")
          .replace(/\D/g, "")
          .slice(0, this.length);
        if (!pasted) return;
        this.applyCode(pasted);
        const focusIdx = Math.min(pasted.length, this.length - 1);
        this.$nextTick(() => this.inputRefs[focusIdx]?.focus?.());
      },
      onFocus(index) {
        this.focusedIndex = index;
        this.$nextTick(() => this.inputRefs[index]?.select?.());
      },
      onBlur() {
        this.focusedIndex = null;
      },
      focus() {
        const firstEmpty = this.digits.findIndex((d) => d === "");
        const idx = firstEmpty === -1 ? 0 : firstEmpty;
        this.inputRefs[idx]?.focus?.();
      },
      clear() {
        this.$emit("update:modelValue", "");
        this.syncDigitInputs("");
        this.$nextTick(() => this.inputRefs[0]?.focus?.());
      },
      restartWebOtp() {
        if (!this.webOtp) return;
        this.stopWebOtpListener();
        this.startWebOtpListener();
      },
    },
  };
</script>
